/**
 * Shared audio processing utilities
 * Used by PlayerComponent.vue and PlayerService.js
 */

/**
 * Creates a distortion curve for WaveShaperNode
 * @param {number} amount - Distortion amount (0-100+)
 * @returns {Float32Array} - Distortion curve
 */
export function makeDistortionCurve(amount) {
    const k = typeof amount === 'number' ? amount : 50;
    const n = 44100;
    const curve = new Float32Array(n);
    const deg = Math.PI / 180;
    for (let i = 0; i < n; ++i) {
        const x = i * 2 / n - 1;
        curve[i] = (3 + k) * x * 20 * deg / (Math.PI + k * Math.abs(x));
    }
    return curve;
}

/**
 * Generates an impulse response for reverb convolver
 * @param {AudioContext} context - Audio context to use
 * @param {number} duration - Duration in seconds (default 2)
 * @param {number} decay - Decay factor (default 2)
 * @returns {AudioBuffer} - Impulse response buffer
 */
export function generateImpulseResponse(context, duration = 2, decay = 2) {
    const sr = context.sampleRate;
    const length = sr * duration;
    const ir = context.createBuffer(2, length, sr);
    const L = ir.getChannelData(0);
    const R = ir.getChannelData(1);

    for (let i = 0; i < length; i++) {
        const t = (length - i) / length;
        L[i] = (Math.random() * 2 - 1) * Math.pow(t, decay);
        R[i] = (Math.random() * 2 - 1) * Math.pow(t, decay);
    }

    return ir;
}

/**
 * Converts an AudioBuffer to WAV format
 * @param {AudioBuffer} buffer - Audio buffer to convert
 * @returns {Blob} - WAV file blob
 */
export function audioBufferToWav(buffer) {
    const numOfChan = buffer.numberOfChannels;
    const length = buffer.length * numOfChan * 2 + 44;
    const bufferArr = new ArrayBuffer(length);
    const view = new DataView(bufferArr);
    const channels = [];
    let i;
    let sample;
    let offset = 0;
    let pos = 0;

    function setUint16(data) {
        view.setUint16(pos, data, true);
        pos += 2;
    }

    function setUint32(data) {
        view.setUint32(pos, data, true);
        pos += 4;
    }

    setUint32(0x46464952); // "RIFF"
    setUint32(length - 8); // file length - 8
    setUint32(0x45564157); // "WAVE"

    setUint32(0x20746d66); // "fmt " chunk
    setUint32(16); // length = 16
    setUint16(1); // PCM (uncompressed)
    setUint16(numOfChan);
    setUint32(buffer.sampleRate);
    setUint32(buffer.sampleRate * 2 * numOfChan); // avg. bytes/sec
    setUint16(numOfChan * 2); // block-align
    setUint16(16); // 16-bit

    setUint32(0x61746164); // "data" - chunk
    setUint32(length - pos - 4); // chunk length

    for (i = 0; i < buffer.numberOfChannels; i++) {
        channels.push(buffer.getChannelData(i));
    }

    while (pos < length) {
        for (i = 0; i < numOfChan; i++) {
            sample = Math.max(-1, Math.min(1, channels[i][offset])); // clamp
            sample = (0.5 + sample < 0 ? sample * 32768 : sample * 32767) | 0; // scale to 16-bit signed int
            view.setInt16(pos, sample, true);
            pos += 2;
        }
        offset++;
    }

    return new Blob([bufferArr], { type: "audio/wav" });
}
